import { useEffect, useState, useCallback } from 'react';

// Extend Window interface to include translationManager
declare global {
    interface Window {
        translationManager: TranslationManager;
    }
}

// Global translation manager - singleton pattern
class TranslationManager {
    cache: any;
    currentLocale: string | null;
    isInitialized: boolean;
    isReady: boolean;
    initPromise: Promise<any> | null;
    subscribers: Set<() => void>;
    loadingPromises: Map<string, Promise<any>>;

    constructor() {
        this.cache = {};
        this.currentLocale = null;
        this.isInitialized = false;
        this.isReady = false;
        this.initPromise = null;
        this.subscribers = new Set();
        this.loadingPromises = new Map(); // Track ongoing loading requests
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        return () => this.subscribers.delete(callback);
    }

    notify() {
        this.subscribers.forEach((callback) => callback());
    }

    // Clear translation cache (useful for development)
    clearCache() {
        console.log('Clearing translation cache...');
        this.cache = {};
        this.isReady = false;
        this.initPromise = null;
        this.currentLocale = null;
        this.loadingPromises.clear(); // Clear ongoing requests
        this.notify();
    }

    // Reset and re-initialize (call after login to load user's language preference)
    async reinitialize() {
        console.log('Reinitializing translation manager...');
        this.cache = {};
        this.isReady = false;
        this.isInitialized = false;
        this.initPromise = null;
        this.currentLocale = null;
        this.loadingPromises.clear();
        return await this.initialize();
    }

    async getUserLanguage() {
        try {
            const { apiFetch } = await import('../utils/api');
            const response = await apiFetch('/api/auth/me', { skipRedirect: true } as any);
            if (response.ok) {
                const user = await response.json();
                console.log('User language from API:', user.language);
                return user.language || 'en';
            }
        } catch (error) {
            console.error('Failed to get user language:', error);
        }
        return 'en';
    }

    async loadTranslations(
        lang,
        namespaces = ['common', 'finance', 'inventory', 'admin', 'people', 'commands'],
    ) {
        // Create a unique key for this request (outside try block for catch access)
        const requestKey = `${lang}-${namespaces.sort().join(',')}`;

        try {
            // Check if all required namespaces are already cached
            const allNamespacesLoaded =
                this.cache[lang] && namespaces.every((ns) => this.cache[lang][ns]);

            if (allNamespacesLoaded && this.currentLocale === lang && this.isReady) {
                console.log(
                    'Using cached translations for:',
                    lang,
                    'namespaces:',
                    namespaces,
                );
                return this.cache[lang];
            }

            // If this exact request is already in progress, wait for it
            if (this.loadingPromises.has(requestKey)) {
                console.log('Waiting for existing request:', requestKey);
                return await this.loadingPromises.get(requestKey);
            }

            // Create and store the loading promise
            const loadingPromise = (async () => {
                const cacheBuster = `${Date.now()}-${Math.random()
                    .toString(36)
                    .substring(2, 11)}`;

                // Load translations for specified namespaces
                const responses = await Promise.all(
                    namespaces.map((namespace) =>
                        fetch(`/locales/${lang}/${namespace}.json?v=${cacheBuster}`),
                    ),
                );
                const translationArrays = await Promise.all(
                    responses.map((response) => response.json()),
                );

                const translations = Object.fromEntries(
                    namespaces.map((namespace, i) => [namespace, translationArrays[i]]),
                );

                return translations;
            })();

            // Store the promise to prevent duplicate requests
            this.loadingPromises.set(requestKey, loadingPromise);

            // Wait for the loading to complete
            const translations = await loadingPromise;

            // Merge with existing cache to support lazy loading
            if (!this.cache[lang]) {
                this.cache[lang] = {};
            }
            Object.assign(this.cache[lang], translations);

            this.currentLocale = lang;
            this.isReady = true;

            // Clean up the loading promise
            this.loadingPromises.delete(requestKey);

            return this.cache[lang];
        } catch (error) {
            console.error('Failed to load translations:', error);

            // Clean up the loading promise on error
            this.loadingPromises.delete(requestKey);

            if (lang !== 'en') {
                return await this.loadTranslations('en', namespaces);
            }
            throw error;
        }
    }

    async initialize() {
        if (this.isInitialized && this.isReady && this.currentLocale) {
            console.log('Already initialized with locale:', this.currentLocale);
            return {
                locale: this.currentLocale,
                translations: this.cache[this.currentLocale],
            };
        }

        if (this.initPromise) {
            console.log('Waiting for existing initialization...');
            return await this.initPromise;
        }

        this.initPromise = (async () => {
            const userLang = await this.getUserLanguage();
            console.log('Initializing translations for locale:', userLang);
            // Load all translations upfront to avoid lazy loading issues
            const translations = await this.loadTranslations(userLang, [
                'common',
                'finance',
                'inventory',
                'admin',
                'people',
                'commands',
            ]);
            this.isInitialized = true;
            this.notify();

            return { locale: userLang, translations };
        })();

        const result = await this.initPromise;
        this.initPromise = null;
        return result;
    }

    async loadNamespace(namespace, lang = this.currentLocale) {
        if (!lang) return;

        if (this.cache[lang] && this.cache[lang][namespace]) {
            console.log('Namespace already loaded:', namespace);
            return this.cache[lang][namespace];
        }

        console.log('Lazy loading namespace:', namespace, 'for language:', lang);
        await this.loadTranslations(lang, [namespace]);
        this.notify();
        return this.cache[lang][namespace];
    }

    async changeLanguage(newLocale) {
        if (newLocale === this.currentLocale) return;

        try {
            const { apiFetch } = await import('../utils/api');
            const response = await apiFetch('/api/auth/me', {
                method: 'PATCH',
                body: JSON.stringify({ language: newLocale }),
            });

            if (response.ok) {
                console.log('Language preference updated to:', newLocale);

                // Clear cache and reload
                this.isReady = false;
                this.initPromise = null;

                await this.loadTranslations(newLocale);
                document.cookie = `i18next=${newLocale}; path=/; max-age=31536000; SameSite=Strict`;

                this.notify();
                console.log('Language changed to:', newLocale);
            }
        } catch (error) {
            console.error('Error changing language:', error);
        }
    }

    translate(key, namespace = 'common', options = {}) {
        if (!this.isReady || !this.currentLocale || !this.cache[this.currentLocale]) {
            return key;
        }

        const keys = key.split('.');
        let value = this.cache[this.currentLocale][namespace];

        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k];
            } else {
                value = undefined;
                break;
            }
        }

        if (value === undefined) {
            if (this.isReady && this.isInitialized) {
                console.warn(
                    `Translation missing for key: ${namespace}.${key} in locale: ${this.currentLocale}`,
                );
            }
            return key;
        }

        if (typeof value === 'string' && options) {
            return value.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                return options[key] || match;
            });
        }

        return value;
    }
}

// Global instance
const translationManager = new TranslationManager();

// Make it globally accessible for debugging (development only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
    window.translationManager = translationManager;
}

// Export reinitialize function for use after login
export function reinitializeTranslations() {
    return translationManager.reinitialize();
}

// Simple i18n hook for handling translations
export function useI18n() {
    const [locale, setLocale] = useState(
        translationManager.currentLocale || 'en',
    );
    const [loading, setLoading] = useState(!translationManager.isReady);
    const [, forceUpdate] = useState({});

    // Subscribe to translation manager updates
    useEffect(() => {
        const unsubscribe = translationManager.subscribe(() => {
            setLocale(translationManager.currentLocale || 'en');
            setLoading(!translationManager.isReady);
            forceUpdate({}); // Force re-render
        });

        return () => {
            unsubscribe();
        };
    }, []);

    // Initialize translations on mount
    useEffect(() => {
        if (!translationManager.isInitialized || !translationManager.isReady) {
            setLoading(true);
            translationManager
                .initialize()
                .then(() => {
                    setLocale(translationManager.currentLocale || 'en');
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        } else {
            setLocale(translationManager.currentLocale || 'en');
            setLoading(false);
        }
    }, []);

    // Translation function (memoized to prevent infinite re-renders)
    const t = useCallback(
        (key, namespace = 'common', options = {}) => {
            return translationManager.translate(key, namespace, options);
        },
        [locale, loading],
    ); // Only recreate when locale or loading state changes

    // Change language function
    const changeLanguage = async (newLocale) => {
        setLoading(true);
        await translationManager.changeLanguage(newLocale);
        setLoading(false);
    };

    // Number formatting
    const formatNumber = (number, options = {}) => {
        const currentLocale = translationManager.currentLocale || 'en';
        return new Intl.NumberFormat(
            currentLocale === 'hu' ? 'hu-HU' : 'en-US',
            options,
        ).format(number);
    };

    // Currency formatting
    const formatCurrency = (amount, currency = 'HUF') => {
        const currentLocale = translationManager.currentLocale || 'en';
        return new Intl.NumberFormat(currentLocale === 'hu' ? 'hu-HU' : 'en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    // Date formatting
    const formatDate = (date, options = {}) => {
        const currentLocale = translationManager.currentLocale || 'en';
        return new Intl.DateTimeFormat(
            currentLocale === 'hu' ? 'hu-HU' : 'en-US',
            options,
        ).format(new Date(date));
    };

    // Expose clearCache for development
    const clearCache = () => {
        translationManager.clearCache();
    };

    return {
        locale,
        t,
        changeLanguage,
        formatNumber,
        formatCurrency,
        formatDate,
        loading,
        isReady: !loading && translationManager.isReady,
        clearCache, // Development helper
    };
}
